import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { EnvelopeSimple, X, Clock } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '');

function PendingRow({ d, lang, onCancel, busy }) {
  return (
    <li className="flex items-center gap-2 text-xs text-sky-900" data-testid={`remote-pending-${d.template_type}`}>
      <Clock size={13} className="shrink-0" />
      <span className="flex-1">
        <b>{d.document_name}</b> — {lang === 'es' ? 'Pendiente firma (email enviado el' : 'Pending signature (email sent on'} {fmt(d.sent_at)}{lang === 'es' ? ' a ' : ' to '}{d.signer?.email})
      </span>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(d)} className="h-7 gap-1 text-red-600 hover:text-red-700" data-testid={`remote-cancel-${d.template_type}`}>
        <X size={12} />{lang === 'es' ? 'Anular' : 'Cancel'}
      </Button>
    </li>
  );
}

// Firma remota VIDsigner (email + OTP SMS) de LOPD e Imágenes desde la pestaña Documentos.
export function RemoteConsentSender({ volunteerId, signerEmail, signerPhone, pending, allSigned, lang, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const es = lang === 'es';
  const missing = !signerEmail ? (es ? 'email' : 'email') : !signerPhone ? (es ? 'móvil' : 'mobile') : null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/vidsigner/send-remote-consents/${volunteerId}`, {}, { withCredentials: true });
      const n = data.sent?.length || 0;
      if (n) toast.success(es ? `${n} documento(s) enviado(s) por email a ${data.sent[0].email}` : `${n} document(s) emailed to ${data.sent[0].email}`);
      else toast.info(es ? 'No había documentos pendientes de enviar' : 'Nothing pending to send');
      setOpen(false); onChanged();
    } catch (err) { toast.error(errText(err, 'Error VIDsigner')); }
    setBusy(false);
  };

  const cancel = async (d) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/vidsigner/documents/${d.doc_gui}/cancel`, {}, { withCredentials: true });
      toast.success(es ? 'Solicitud de firma anulada' : 'Signature request cancelled');
      onChanged();
    } catch (err) { toast.error(errText(err, 'Error VIDsigner')); }
    setBusy(false);
  };

  return (
    <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 space-y-2" data-testid="remote-consent-sender">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-sky-900">
          {es ? 'Firma remota: el voluntario recibe LOPD e Imágenes por email y firma con un código OTP enviado a su móvil.' : 'Remote signature: the volunteer receives LOPD and Images by email and signs with an SMS OTP.'}
        </p>
        <Button size="sm" disabled={allSigned || busy} onClick={() => setOpen(true)} className="bg-sky-700 hover:bg-sky-800 text-white rounded-sm gap-2 h-8 shrink-0" data-testid="remote-consent-send-btn">
          <EnvelopeSimple size={14} />{es ? 'Enviar LOPD + Imágenes por email' : 'Email LOPD + Images'}
        </Button>
      </div>
      {pending.length > 0 && <ul className="space-y-1">{pending.map(d => <PendingRow key={d.doc_gui} d={d} lang={lang} onCancel={cancel} busy={busy} />)}</ul>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="remote-consent-dialog">
          <DialogHeader>
            <DialogTitle>{es ? 'Enviar a firmar por email' : 'Send for remote signature'}</DialogTitle>
            <DialogDescription>{es ? 'Se enviarán los documentos LOPD e Imágenes que no estén firmados ni pendientes.' : 'LOPD and Images documents not yet signed or pending will be sent.'}</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <p><span className="text-stone-500">{es ? 'Email:' : 'Email:'}</span> <b data-testid="remote-consent-email">{signerEmail || '—'}</b></p>
            <p><span className="text-stone-500">{es ? 'Móvil (OTP SMS):' : 'Mobile (SMS OTP):'}</span> <b data-testid="remote-consent-phone">{signerPhone || '—'}</b></p>
            {missing && <p className="text-red-600 text-xs" data-testid="remote-consent-missing">{es ? `Falta el ${missing} del firmante. Complétalo en Datos personales antes de enviar.` : `Signer ${missing} is missing.`}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button disabled={busy || !!missing} onClick={send} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="remote-consent-confirm">{busy ? '...' : (es ? 'Enviar' : 'Send')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
